'use client'
import { createContext, useContext, useEffect, useState } from 'react'
import { translations, type Lang, type T } from '@/lib/i18n'

type LanguageCtx = {
  lang: Lang
  setLang: (l: Lang) => void
  setLangPreference: (l: Lang) => void
  t: T
  isRTL: boolean
}

const LanguageContext = createContext<LanguageCtx>({
  lang: 'en',
  setLang: () => {},
  setLangPreference: () => {},
  t: translations.en,
  isRTL: false,
})

function writeLangCookie(l: Lang) {
  document.cookie = `trend-lang=${l}; path=/; max-age=31536000; SameSite=Lax`
}

export function LanguageProvider({
  children,
  initialLang = 'en',
}: {
  children: React.ReactNode
  initialLang?: Lang
}) {
  // Seed state from the server-detected language (URL via proxy.ts x-lang;
  // cookie for 404s) so the first render matches the server HTML (lang + dir
  // attributes). The URL is the source of truth — no localStorage override,
  // otherwise /fa could flip back to a stale saved language after hydration.
  const [lang, setLangState] = useState<Lang>(initialLang)

  // Context + cookie only — no URL mutation. Used on blog pages, where a
  // language switch is a REAL navigation (router.push to the counterpart URL)
  // and a history.replaceState would fight the router.
  const setLangPreference = (l: Lang) => {
    setLangState(l)
    // Cookie is only read on paths without a language URL (the 404 page).
    writeLangCookie(l)
  }

  const setLang = (l: Lang) => {
    setLangPreference(l)
    // Keep the URL in sync (/ ↔ /fa) without a server round-trip — the App
    // Router supports native shallow history updates.
    window.history.replaceState(null, '', l === 'fa' ? '/fa' : '/')
  }

  useEffect(() => {
    const isRTL = lang === 'fa'
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
    document.documentElement.lang = lang
    document.body.style.fontFamily = 'var(--font-meem), sans-serif'
  }, [lang])

  return (
    <LanguageContext.Provider
      value={{
        lang,
        setLang,
        setLangPreference,
        t: translations[lang] as T,
        isRTL: lang === 'fa',
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  return useContext(LanguageContext)
}
